/**
 * tenant          => all users who are members of the tenant organization group
 *
 * owner           => only the item's owner (its creator). The 'groups' and 'users'
 *                    lists are not consulted at this level.
 *
 * selected_groups => users who are a member of at least one group in 'groups',
 *                    OR are listed individually in 'users'
 */
export const itemAccessControlLevels = <const>['tenant', 'owner', 'selected_groups'];
export type ItemAccessControlLevels = typeof itemAccessControlLevels[number];

/**
 * This interface defines the data model used to impose different types of access
 * restrictions on an item.
 */
export interface ItemAccessControl {
  /**
   * The level of restriction for this item.
   * If this property exists, only certain parameters will be checked based on its value. For example, if the level is 'selected_groups',
   * the 'groups' parameter will determine if access should be allowed.
   * If this property doesn't exist, each parameter will be evaluated to determine access.
   */
  level?: ItemAccessControlLevels;
  /**
   * List of group IDs this item is accessible to. Will be checked if 'level' is undefined or 'selected_groups'.
   */
  groups?: string[];
  /**
   * List of individual user IDs granted access, evaluated together with `groups` (logical OR).
   * Checked when `level` is undefined or `selected_groups`.
   */
  users?: string[];
}
